import { useEffect, useMemo, useState } from 'react';
import { X, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { authedFetch } from '../lib/api';
import { ErrorBanner } from './ErrorBanner';

interface Props {
  tenantCode: string;
  chatId: string;
  subscriptionId: number;
  onClose: () => void;
}

interface OrderRow {
  order_id: number;
  date: string;
  status: string;
  type: string;
  amount: number;
  is_paid: boolean;
  edit_url?: string;
}

interface LineRow {
  id: number;
  order_id: number;
  phone_number: string | null;
  first_name: string | null;
  last_name: string | null;
  physical_iccid: string | null;
  imsi: string | null;
  activated: boolean;
  esim: boolean;
  is_cancelled: boolean;
  is_frozen: boolean;
  ported: boolean;
  last_connection: string | null;
}

interface ItemRow {
  name: string;
  quantity: number;
  price: number;
}

interface DebtResponse {
  configured: boolean;
  gated?: string;
  reason?: string;
  subscription_id?: number;
  status?: string;
  currency?: string;
  total_debt?: number;
  monthly_amount?: number;
  billing_day?: number | string | null;
  days_overdue?: number | null;
  missed_months?: number;
  has_missed_payment?: boolean | null;
  last_payment_date?: string | null;
  next_payment_date?: string | null;
  parent_order?: { order_id: number; status: string; date: string; total: number } | null;
  customer?: { id: number; name: string; email: string } | null;
  orders_data?: OrderRow[];
  order_items_details?: ItemRow[];
  marked?: { reason: string | null; date: string | null };
}

interface LinesResponse {
  configured: boolean;
  gated?: string;
  items?: LineRow[];
  count?: number;
}

export function WpDebtLedgerModal({ tenantCode, chatId, subscriptionId, onClose }: Props) {
  const { user } = useAuth();
  const [debt, setDebt] = useState<DebtResponse | null>(null);
  const [lines, setLines] = useState<LinesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const baseQs = `?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}`;
        const [d, l] = await Promise.all([
          authedFetch<DebtResponse>(`/agent/wp/debt${baseQs}&subscriptionId=${subscriptionId}`),
          authedFetch<LinesResponse>(`/agent/wp/lines${baseQs}&subscriptionId=${subscriptionId}`),
        ]);
        if (cancelled) return;
        setDebt(d);
        setLines(l);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantCode, chatId, subscriptionId, user]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const totalDebt = debt?.total_debt ?? 0;
  const currency = debt?.currency ?? '';
  const isOverdue = totalDebt > 0.009;

  const totals = useMemo(() => {
    const orders = debt?.orders_data ?? [];
    const paid = orders.filter((o) => o.is_paid).reduce((s, o) => s + o.amount, 0);
    const unpaid = orders.filter((o) => !o.is_paid).reduce((s, o) => s + o.amount, 0);
    return { count: orders.length, paid, unpaid };
  }, [debt]);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-baseline gap-3">
            <h2 className="text-base font-semibold text-gray-900">
              Debt ledger
            </h2>
            <span className="text-xs text-gray-500">Subscription #{subscriptionId}</span>
            {debt?.status && (
              <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${statusColor(debt.status)}`}>
                {debt.status}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && <div className="text-xs text-gray-500">Loading ledger…</div>}
          <ErrorBanner error={error} label="Error" className="text-xs text-red-600" />
          {debt?.gated === 'not_verified' && (
            <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-l-2 border-amber-400 rounded">
              Customer not verified. Verify with last-4 CC to view debt.
            </div>
          )}

          {debt && !debt.gated && (
            <>
              <section
                className={`rounded-md border p-4 ${
                  isOverdue ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Outstanding</div>
                    <div className={`text-2xl font-bold ${isOverdue ? 'text-red-700' : 'text-green-700'}`}>
                      {totalDebt.toFixed(2)} {currency}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {isOverdue ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded">
                        <AlertTriangle className="w-3.5 h-3.5" /> Overdue
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded">
                        <CheckCircle2 className="w-3.5 h-3.5" /> No debt
                      </span>
                    )}
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                  <Stat label="Monthly" value={fmtCur(debt.monthly_amount, currency)} />
                  <Stat label="Billing day" value={debt.billing_day ?? '–'} />
                  <Stat label="Days overdue" value={debt.days_overdue ?? '–'} />
                  <Stat label="Missed months" value={debt.missed_months ?? 0} />
                  <Stat label="Last paid" value={fmtDate(debt.last_payment_date)} />
                  <Stat label="Next" value={fmtDate(debt.next_payment_date)} />
                  {debt.customer && <Stat label="Customer" value={debt.customer.name} />}
                  {debt.customer?.email && <Stat label="Email" value={debt.customer.email} />}
                </dl>
                {debt.marked?.reason && (
                  <div className="mt-3 text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                    Marked: {debt.marked.reason}
                    {debt.marked.date ? ` (${fmtDate(debt.marked.date)})` : ''}
                  </div>
                )}
              </section>

              <section>
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Payment ledger</h3>
                  <span className="text-[11px] text-gray-500">
                    {totals.count} orders · paid {totals.paid.toFixed(2)} {currency} · unpaid{' '}
                    {totals.unpaid.toFixed(2)} {currency}
                  </span>
                </div>
                {debt.orders_data && debt.orders_data.length > 0 ? (
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-medium">Order</th>
                          <th className="text-left px-3 py-1.5 font-medium">Date</th>
                          <th className="text-left px-3 py-1.5 font-medium">Type</th>
                          <th className="text-left px-3 py-1.5 font-medium">Status</th>
                          <th className="text-right px-3 py-1.5 font-medium">Amount</th>
                          <th className="px-2 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {debt.orders_data.map((o) => (
                          <tr key={o.order_id} className="border-t border-gray-100">
                            <td className="px-3 py-1.5 text-gray-900 font-medium">#{o.order_id}</td>
                            <td className="px-3 py-1.5 text-gray-700">{o.date.slice(0, 10)}</td>
                            <td className="px-3 py-1.5 text-gray-600">{o.type}</td>
                            <td className="px-3 py-1.5">
                              <span
                                className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${
                                  o.is_paid
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {o.status}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-right font-medium">
                              {o.amount.toFixed(2)}
                            </td>
                            <td className="px-2 py-1.5">
                              {o.edit_url && (
                                <a
                                  href={o.edit_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 italic">No orders recorded.</div>
                )}
              </section>

              {lines && lines.items && lines.items.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Lines on this subscription</h3>
                  <div className="space-y-1.5">
                    {lines.items.map((l) => (
                      <div
                        key={l.id}
                        className="border border-gray-200 rounded p-2 text-xs flex flex-wrap items-center gap-3"
                      >
                        <span className="font-medium text-gray-900" dir="ltr">
                          {l.phone_number ?? `Line #${l.id}`}
                        </span>
                        {l.physical_iccid && (
                          <span className="text-[10px] text-gray-500" dir="ltr">
                            ICCID {l.physical_iccid}
                          </span>
                        )}
                        {l.esim && (
                          <span className="text-[10px] uppercase px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                            eSIM
                          </span>
                        )}
                        <span
                          className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${
                            l.is_cancelled
                              ? 'bg-gray-200 text-gray-600'
                              : l.is_frozen
                              ? 'bg-amber-100 text-amber-700'
                              : l.activated
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {l.is_cancelled
                            ? 'cancelled'
                            : l.is_frozen
                            ? 'frozen'
                            : l.activated
                            ? 'active'
                            : 'inactive'}
                        </span>
                        {l.ported && (
                          <span className="text-[10px] uppercase px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                            ported
                          </span>
                        )}
                        {(l.first_name || l.last_name) && (
                          <span className="ml-auto text-gray-500">
                            {[l.first_name, l.last_name].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {debt.order_items_details && debt.order_items_details.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Plan items</h3>
                  <ul className="space-y-1 text-xs">
                    {debt.order_items_details.map((i, ix) => (
                      <li key={ix} className="flex items-center justify-between border-b border-gray-100 pb-1">
                        <span className="text-gray-800" dir="auto">{i.name}</span>
                        <span className="text-gray-500">
                          ×{i.quantity} · {i.price.toFixed(2)} {currency}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        <footer className="flex justify-end px-5 py-2 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-gray-900 font-medium" dir="auto">{value}</div>
    </div>
  );
}

function fmtCur(n: number | undefined | null, currency: string): string {
  if (n === undefined || n === null) return '–';
  return `${n.toFixed(2)} ${currency}`.trim();
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '–';
  return s.length > 10 ? s.slice(0, 10) : s;
}

function statusColor(s: string): string {
  if (s === 'active') return 'bg-green-100 text-green-700';
  if (s === 'on-hold' || s === 'pending') return 'bg-yellow-100 text-yellow-700';
  if (s === 'cancelled' || s === 'expired') return 'bg-gray-200 text-gray-600';
  return 'bg-blue-100 text-blue-700';
}
